/**
 * Token 刷新拦截器。
 *
 * 设计说明：只有 refresh 本身失败才代表会话失效；refresh 成功后重放请求返回的业务错误
 * 必须原样抛出，避免把 403/404/500 等业务问题误判成登录失效。
 */

import type { Interceptor } from '@connectrpc/connect'
import { Code, ConnectError } from '@connectrpc/connect'
import { AuthReplaySession } from '@/services/auth-replay'
import { useAuthStore } from '@/stores/auth'

/** 当前正在进行的 refresh Promise；并发 401 共享同一个刷新请求。 */
let refreshPromise: Promise<string> | null = null

/**
 * 判断请求是否为认证端点。
 *
 * @param url Connect 请求 URL。
 * @returns 认证端点返回 true。
 */
function isAuthEndpoint(url: string): boolean {
  return url.includes('RefreshTokens') || url.includes('Logout') || url.includes('Login')
}

/**
 * 判断错误是否代表未认证。
 *
 * @param error 待判断错误。
 * @returns 未认证错误返回 true。
 */
function isUnauthenticatedError(error: unknown): error is ConnectError {
  return error instanceof ConnectError && error.code === Code.Unauthenticated
}

/**
 * 刷新 access token，并对并发刷新做去重。
 *
 * @returns 新 access token。
 */
function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = useAuthStore().refreshSession().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

export const refreshInterceptor: Interceptor = (next) => async (req) => {
  if (isAuthEndpoint(req.url)) {
    return next(req)
  }

  const replaySession = new AuthReplaySession({
    requestKind: 'unary',
    operation: req.url,
  })

  while (true) {
    try {
      return await next(req)
    } catch (error) {
      if (!isUnauthenticatedError(error)) {
        throw error
      }

      replaySession.recordUnauthorizedAndAssertCanReplay({
        code: error.code,
        cause: error,
      })

      const newToken = await replaySession.refreshAuthorization(refreshAccessToken)
      req.header.set('Authorization', `Bearer ${newToken}`)
    }
  }
}
